import {useAuth0} from '@auth0/auth0-react';
import {Table, Button, Message, Empty, Tooltip} from "@arco-design/web-react";
import {IconEdit, IconDelete} from "@arco-design/web-react/icon";
import styled from "styled-components";
import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {DeleteApi, FetchApi, PostApi, SaveApi} from '../Api/AuthenticatedApi';
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import Content from "../Layout/Content";
import LoadingState from "../Layout/LoadingState";
import SummaryCard from "../Layout/SummaryCard";
import ContactListAddEditDialog from "./ContactListAddEditDialog";
import ContactListDeleteDialog from './ContactListDeleteDialog';


// MUI -> Arco. The MUI Table is rewritten into an Arco Table columns config +
// data source; the Toolbar becomes a styled flex bar, the count Link a clickable
// span in the primary color, and the edit/delete Links become Arco icon Buttons
// (shape="circle" type="text"). CircularProgress -> Spin. makeStyles ->
// styled-components; theme.spacing(1) = 8px. Logic + data flow are untouched.

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  min-height: 64px;
  padding: 0 8px;
`;

const Grow = styled.div`
  flex-grow: 1;
`;

// countsOf is the list's members by status; an older server sends only the
// total, which is shown as active.
const countsOf = (row) => row.counts || { active: row.count || 0, pending: 0, unsubscribed: 0, suppressed: 0, new: 0 };

// A count shown quietly, with a dash for none.
const Muted = ({ n }) => (
    <span style={{ color: "var(--color-text-3)" }}>{n ? n.toLocaleString() : "—"}</span>
);

const CountLink = styled.span`
  cursor: pointer;
  color: rgb(var(--arcoblue-6));
`;

export default function ContactLists() {

    // ----------------------------
    // Add Edit Dialog
    // ----------------------------
    const [openAddEditDialog, setOpenAddEditDialog] = React.useState(false);

    const [listName, setListName] = React.useState("");
    const [listDescription, setListDescription] = React.useState("");
    const [listId, setListId] = React.useState("");

    const handleCloseAddEditDialog = () => {
        setListName("");
        setListDescription("");
        setListId("");
        setOpenAddEditDialog(false);
    }

    const handleOpenAddEditDialogForEdit = (row) => {
        setListName(row.name);
        setListDescription(row.description);
        setListId(row.id);
        setOpenAddEditDialog(true);
    }

    const handleOpenAddEditDialogForAdd = () => {
        setListName("");
        setListDescription("");
        setListId("");
        setOpenAddEditDialog(true);
    }
    const handleAddEditContactList = () => {
        if (listId) {
            saveList({ name: listName, description: listDescription, id: listId });
        } else {
            createList({ name: listName, description: listDescription });
        }
    }

    // ----------------------------
    // Delete Dialog
    // ----------------------------
    const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);

    const handleOpenDeleteDialog = (row) => {
        setListName(row.name);
        setListId(row.id);
        setOpenDeleteDialog(true);
    }

    const handleCloseDeleteDialog = () => {
        setListId("");
        setListName("");
        setListDescription("");
        setOpenDeleteDialog(false);
    }

    const handleDeleteContactList = () => {
        deleteList(listId);
    }


    let history = useNavigate();
    const { getAccessTokenSilently } = useAuth0();

    const [rows, setRows] = useState([]);
    const [refreshContactLists, setRefreshContactLists] = React.useState(true);

    const [loading, setLoading] = useState(true);



    function saveList(data) {
        (async () => {
            let url = "/api/contact-lists/" + data.id
            SaveApi(getAccessTokenSilently, url,
                (data) => {
                    setLoading(false);
                    setRefreshContactLists(true);
                    handleCloseAddEditDialog();
                    Message.success("Contact list updated");
                },
                (response) => {
                    setLoading(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    Message.error("Failed to save contact list");
                }, data);
        })();
    }

    function createList(data) {
        (async () => {
            let url = "/api/contact-lists/"
            PostApi(getAccessTokenSilently, url,
                (data) => {
                    setLoading(false);
                    setRefreshContactLists(true);
                    handleCloseAddEditDialog();
                    Message.success("Contact list created");
                },
                (response) => {
                    setLoading(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    Message.error("Failed to create contact list");
                }, data);
        })();
    }

    function deleteList(id) {
        (async () => {
            let url = "/api/contact-lists/" + id;
            DeleteApi(getAccessTokenSilently, url,
                (data) => {
                    setLoading(false);
                    setRefreshContactLists(true);
                    handleCloseDeleteDialog();
                    Message.success("Contact list deleted");
                },
                (response) => {
                    setLoading(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    Message.error("Failed to delete contact list");
                }, { "contact_list_id": id });
        })();
    }

    useEffect(() => {
        (async () => {
            if (refreshContactLists) {
                let url = "/api/contact-lists/"
                FetchApi(getAccessTokenSilently, url,
                    (data) => {
                        setLoading(false);
                        setRows(data);
                        setRefreshContactLists(false);
                    },
                    (response) => {
                        setLoading(false);
                        history("/login");
                    }, (response) => {
                        setLoading(false);
                        console.error("Failed to load data", response);
                    })
            }

        })();
    }, [setRows, setLoading, history, getAccessTokenSilently, refreshContactLists])


    // console.log("Setting up Campaigns!")

    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            // Opens Contacts filtered to everyone on the list.
            render: (name, row) => (
                <CountLink onClick={() => history("/contacts?initialContactListFilterId=" + row.id)}>{name}</CountLink>
            ),
        },
        {
            title: <Tooltip content="Confirmed and still subscribed: who a broadcast to this list reaches">Active</Tooltip>,
            render: (_, row) => <strong>{countsOf(row).active.toLocaleString()}</strong>,
        },
        {
            title: <Tooltip content="Joined but haven't clicked Confirm yet">Pending</Tooltip>,
            render: (_, row) => <Muted n={countsOf(row).pending} />,
        },
        {
            title: <Tooltip content="Left this list, or unsubscribed from everything">Unsubscribed</Tooltip>,
            render: (_, row) => <Muted n={countsOf(row).unsubscribed} />,
        },
        {
            title: <Tooltip content="Their address bounced or they marked your email as spam; never mailed">Bounced</Tooltip>,
            render: (_, row) => <Muted n={countsOf(row).suppressed} />,
        },
        {
            title: <Tooltip content="Joined in the last 30 days, whatever their status now">New (30 days)</Tooltip>,
            render: (_, row) => {
                const n = countsOf(row).new;
                return n > 0 ? <span style={{ color: "rgb(var(--green-6))" }}>+{n.toLocaleString()}</span> : <Muted n={0} />;
            },
        },
        {
            title: "Description",
            dataIndex: "description",
            render: (description) => <div>{description}</div>,
        },
        {
            title: "",
            render: (_, row) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Button
                        shape="circle"
                        type="text"
                        icon={<IconEdit />}
                        onClick={() => handleOpenAddEditDialogForEdit(row)}
                    />
                    <Button
                        shape="circle"
                        type="text"
                        icon={<IconDelete />}
                        onClick={() => handleOpenDeleteDialog(row)}
                    />
                </div>
            ),
        },
    ];

    if (loading) {
        return <LoadingState />;
    }

    return (
        <Content>
            <SimpleBreadcrumbs
                elements={
                    [
                        { name: "Contact Lists" },
                    ]
                }
            />

            <Toolbar>
                <Grow />
                <Button
                    type="primary"
                    icon={<IconEdit />}
                    onClick={handleOpenAddEditDialogForAdd}
                >
                    Add
                </Button>
            </Toolbar>

            <SummaryCard
                title={"Contact Lists"}
                subTitle={"Who's on each list, and where they stand"}
            >
                <>
                    <Table
                        size="small"
                        columns={columns}
                        data={rows}
                        rowKey="id"
                        pagination={false}
                        border={false}
                        noDataElement={
                            <Empty description="No contact lists yet">
                                <Button type="primary" style={{ marginTop: 12 }} onClick={handleOpenAddEditDialogForAdd}>
                                    Create your first list
                                </Button>
                            </Empty>
                        }
                    />


                    <ContactListAddEditDialog
                        open={openAddEditDialog}

                        onSave={handleAddEditContactList}
                        onClose={handleCloseAddEditDialog}

                        listName={listName}
                        setListName={setListName}

                        listDescription={listDescription}
                        setListDescription={setListDescription}
                        listId={listId}
                    />

                    <ContactListDeleteDialog
                        open={openDeleteDialog}

                        onDelete={handleDeleteContactList}
                        onClose={handleCloseDeleteDialog}

                    />

                </>
            </SummaryCard>
        </Content>
    )

}
